/**
 * "Age of the signal" — a generic derived series shared by the CUE indicators
 * that expose a bars-since companion column (Squeeze, Daily/Weekly Bongo,
 * Daily/Weekly RSMA).
 *
 * 1-based: the bar a state begins / a signal fires counts as 1 (so a fresh flip
 * reads 1, the next bar 2, …) — matching how the bars read on the chart and the
 * PMC/RSMA bars-since-cross counts. Two modes:
 *   "state"  age[i] = bars the CURRENT state value has held, counting the bar it
 *            began as 1. Any change in the (non-null) value resets to 1. `null`
 *            during the parent's warmup.
 *   "fire"   age[i] = bars since the LAST fire (parent value ≠ 0 / non-null —
 *            handles signed +1/-1 fires too), counting the firing bar as 1.
 *            `null` before the first fire.
 *
 * Index-aligned to the parent series, so each "+age" indicator is just another
 * `SharedIndicatorDef` whose computeSeries = ageSeries(parentSeries, mode).
 */
export function ageSeries(
  series: ReadonlyArray<number | null>,
  mode: "state" | "fire" = "state",
): (number | null)[] {
  const n = series.length;
  const out: (number | null)[] = new Array(n).fill(null);

  if (mode === "fire") {
    let last = -1;
    for (let i = 0; i < n; i++) {
      const v = series[i];
      if (v != null && v !== 0) {
        last = i;
        out[i] = 1;
      } else if (last >= 0) {
        out[i] = i - last + 1;
      }
    }
    return out;
  }

  // "state": bars the current (non-null) value has held (started bar = 1).
  let prev: number | undefined;
  let startIdx = -1;
  for (let i = 0; i < n; i++) {
    const v = series[i];
    if (v == null || !Number.isFinite(v)) {
      out[i] = null;
      continue;
    }
    if (prev === undefined || v !== prev) {
      startIdx = i;
      prev = v;
    }
    out[i] = i - startIdx + 1;
  }
  return out;
}
